import { naturalCompare, type NoteIndexEntry } from '@kartothek/shared';
import { useEffect, useId, useMemo, useRef, useState } from 'react';
import { useNoteIndex, useTypes } from '../hooks';
import { classNames, fuzzyFilter } from '../util';

export interface ComboOption {
  key: string;
  /** Wird ins Feld übernommen und durchsucht. */
  title: string;
  folder?: string;
  updatedAt?: string;
  /** Rechts in der Liste (z. B. Typ oder Preis). */
  hint?: string;
  color?: string;
}

/**
 * Freitextfeld mit Vorschlagsliste im Stil von Strg+K.
 * ↑/↓ wählt, Enter übernimmt den markierten Vorschlag (sonst `onEnter`), Esc schließt.
 */
export function Combobox<T extends ComboOption>(props: {
  value: string;
  onChange: (text: string) => void;
  options: readonly T[];
  onPick: (option: T) => void;
  /** Enter ohne (neuen) Vorschlag – z. B. Formular absenden. Ohne Angabe: Standardverhalten. */
  onEnter?: () => void;
  onBlur?: () => void;
  placeholder?: string;
  className?: string;
  inputRef?: React.Ref<HTMLInputElement>;
  autoFocus?: boolean;
  /** Ohne Eingabe alle Vorschläge zeigen (Standard) oder erst ab dem ersten Zeichen. */
  showAllWhenEmpty?: boolean;
  limit?: number;
  'aria-label'?: string;
}) {
  const id = useId();
  const [open, setOpen] = useState(false);
  const [sel, setSel] = useState(-1);
  const listRef = useRef<HTMLUListElement>(null);
  const { value, options } = props;
  const limit = props.limit ?? 50;

  const results = useMemo(() => {
    if (!value.trim()) return props.showAllWhenEmpty === false ? [] : options.slice(0, limit);
    return fuzzyFilter(options, value, limit);
  }, [options, value, limit, props.showAllWhenEmpty]);
  useEffect(() => setSel(value.trim() ? 0 : -1), [value]);
  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [sel]);

  const pick = (o: T) => {
    setOpen(false);
    props.onPick(o);
  };

  const onKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      if (!open) return setOpen(true);
      setSel((s) => Math.min(results.length - 1, s + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSel((s) => Math.max(-1, s - 1));
    } else if (e.key === 'Enter') {
      const hit = open ? results[sel] : undefined;
      if (hit && hit.title !== value) {
        e.preventDefault();
        pick(hit);
      } else if (props.onEnter) {
        e.preventDefault();
        setOpen(false);
        props.onEnter();
      }
    } else if (e.key === 'Escape') {
      if (open) e.stopPropagation();
      setOpen(false);
    }
  };

  return (
    <div className={classNames('combo', props.className)}>
      <input
        ref={props.inputRef}
        value={value}
        role="combobox"
        aria-label={props['aria-label']}
        aria-expanded={open}
        aria-controls={id}
        aria-autocomplete="list"
        autoFocus={props.autoFocus}
        placeholder={props.placeholder}
        onChange={(e) => {
          props.onChange(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onClick={() => setOpen(true)}
        onBlur={() => {
          setOpen(false);
          props.onBlur?.();
        }}
        onKeyDown={onKeyDown}
      />
      {open && results.length > 0 && (
        <ul className="combo-list" id={id} role="listbox" ref={listRef}>
          {results.map((o, i) => (
            <li
              key={o.key}
              role="option"
              aria-selected={i === sel}
              className={classNames(i === sel && 'sel')}
              onMouseEnter={() => setSel(i)}
              // mousedown statt click: sonst schließt der Blur des Eingabefelds die Liste vorher
              onMouseDown={(e) => {
                e.preventDefault();
                pick(o);
              }}
            >
              {o.color !== undefined && <span className="type-dot" style={{ background: o.color }} />}
              <span className="quick-title">{o.title}</span>
              {o.hint && <span className="muted small">{o.hint}</span>}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export type NoteOption = ComboOption & { note: NoteIndexEntry };

/** Vorschläge aus den Notizen der Kampagne, optional auf Typen beschränkt; mit Typfarbe und -name. */
export function useNoteOptions(cid: string, typeKeys?: readonly string[]): NoteOption[] {
  const index = useNoteIndex(cid);
  const types = useTypes(cid);
  const key = typeKeys?.join(',') ?? '';
  return useMemo(() => {
    const allowed = typeKeys?.length ? new Set(typeKeys) : null;
    return (index.data ?? [])
      .filter((n) => !allowed || allowed.has(n.typeKey))
      .sort((a, b) => naturalCompare(a.title, b.title))
      .map((n) => {
        const type = types.byKey.get(n.typeKey);
        return { key: n.id, title: n.title, folder: n.folder, hint: type?.label ?? n.folder, color: type?.color ?? '', note: n };
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index.data, types.byKey, key]);
}
